import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { getPhotoData } from "./helpers";
import {
  analyticsSearchParamsTypes,
  setAnalyticsSearchParam
} from "../helpers/analytics";

import { useGetUrlParams } from "../hooks/useGetUrlParams";
import useGetFileStackKeys from "../hooks/useGetFileStackKeys";
import {
  setSessionStorage,
  getSessionStorage
} from "../sessionAndLocalStorage";
import { imageUploadSlice } from "../store/slices/ImageUploads";
import {
  getAppraisalData,
  getHasError,
  selectAppraisal,
  selectImageUploadData
} from "../store/selectors";
import { appraisalSlice } from "../store/slices/Appraisal";
import { ConnectionLost } from "../components/ConnectionLost";
import { errorMessages } from "../data/filterTypes";
import { useSourceSystem } from "../hooks/useSourceSystem";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeviceFeatures } from "../hooks/useDeviceFeatures";

export default function Wrapper({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const state = useSelector((state) => state);
  const hasError = useSelector(getHasError);
  const storedImages = getSessionStorage("images") || [];
  const {
    photosRequired: requiredCategories,
    questionsAndAnswers,
    valuation,
    promoApplied,
    isOnline,
    sourceSystemAnalyticsParam
  } = useSelector(getAppraisalData);
  const { error } = useSelector(selectAppraisal);
  const { returnToGallery } = useSelector(selectImageUploadData);
  const {
    sourceSystem: { analyticSearchParam, analyticsJourney }
  } = useSourceSystem();

  const dispatch = useDispatch();

  const isOnErrorPage = useCallback(() => {
    return location.pathname === "/value-my-vehicle/error";
  }, [location.pathname]);

  useEffect(() => {
    setSessionStorage("state", state);
  }, [location.pathname, state]);

  useEffect(() => {
    // Update journey property in google analytics data layer when the analyticsJourney prop is set in source system
    if (analyticsJourney) {
      const dataLayerObject = {
        journey: analyticsJourney
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(dataLayerObject);
    }
  }, [analyticsJourney]);

  useGetUrlParams(location);
  useGetFileStackKeys();
  useDeviceFeatures();

  useEffect(() => {
    setAnalyticsSearchParam(
      window.location,
      sourceSystemAnalyticsParam,
      analyticsSearchParamsTypes.VALUEJOURNEY,
      navigate
    );
  }, [location, analyticSearchParam, sourceSystemAnalyticsParam]);

  useEffect(() => {
    if (!isOnErrorPage()) {
      if (storedImages.length) {
        if (!returnToGallery) {
          dispatch(
            imageUploadSlice.actions.setImagesWithoutBlobs(storedImages)
          );
        }
      }
      if (error) {
        dispatch(appraisalSlice.actions.clearError());
      }
    }
    dispatch(appraisalSlice.actions.clearQuestionValidation());
    // eslint-disable-next-line
  }, [dispatch, location]);

  useEffect(() => {
    if (requiredCategories && Object.keys(requiredCategories).length > 0) {
      for (const [key, value] of Object.entries(requiredCategories)) {
        if (value > 0) {
          const answerId = questionsAndAnswers[key][0];
          const photoData = getPhotoData(key, value, answerId);
          photoData.questionId = key;

          dispatch(imageUploadSlice.actions.setRequiredPhotoData(photoData));
        }
        if (value === 0) {
          dispatch(imageUploadSlice.actions.removeRequiredPhotoData(key));
        }
      }
    }
  }, [dispatch, questionsAndAnswers, requiredCategories]);

  useEffect(() => {
    if (hasError) {
      const { error } = state.appraisal;
      if (error !== errorMessages.NETWORKERROR && !isOnErrorPage()) {
        navigate("/error");
      }
    }
  }, [hasError, state, isOnErrorPage]);

  useEffect(() => {
    if (!valuation?.promotionAppliedToValuation && promoApplied) {
      dispatch(appraisalSlice.actions.setPromoApplied(false));
    }
  }, [dispatch, promoApplied, valuation]);

  return (
    <>
      <ConnectionLost isOnline={isOnline} />
      {children}
    </>
  );
}

Wrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element
  ]).isRequired
};
